import { MouseEvent, useCallback, useState } from 'react';
import { Box, Typography } from '@mui/material';
import classnames from 'classnames';
import { noop } from 'lodash';
import { Post, ReactionType } from '../../../../generated/graphql';
import { PostHeader } from './PostHeader';
import { PostContent } from './PostContent';
import { PostReactions } from './PostReactions';
import { PostFooter } from './PostFooter';
import { SensitiveContent } from './SensitiveContent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbtack } from '@fortawesome/pro-solid-svg-icons';
import { CommunityCommentsSection } from './CommunityCommentsSection';
import { useTrackPostViewed } from './useTrackPostViewed';
import { communityAnalytics } from '../utils/communityAnalytics';

const ExpandCommentsTrigger = {
  CommentsCount: 'Comment Count',
  AddComment: 'Add Comment',
} as const;

type ExpandCommentsTrigger =
  (typeof ExpandCommentsTrigger)[keyof typeof ExpandCommentsTrigger];

type PostAuthor = {
  id: number;
  username: string;
  badges: string[];
  profilePicture: string;
  email: string;
  marketing: boolean;
};

type PostData = {
  id: string;
  text: string;
  createdAt: Date;
  author: PostAuthor;
  media: Post['media'];
  reactions: Post['reactions'];
  isSensitiveContent: boolean;
};

type CommunityPostCellProps = {
  influencerId: number;
  communityId: number;
  isUserCommunityOwner: boolean;
  postData: PostData;
  onDelete?: () => void;
  onBlock?: (communityId: number, userId: number) => void;
  onToggleReaction?: (reactionType: ReactionType) => void;
  onReplyInDM?: () => void;
  isPinned?: boolean;
  onPin?: () => void;
  pinLimitReached?: boolean;
  isHighlighted?: boolean;
  isDmFeatureEnabled?: boolean;
  defaultExpanded?: boolean;
};

export const CommunityPostCell = ({
  influencerId,
  communityId,
  isUserCommunityOwner,
  postData,
  onDelete = noop,
  onBlock = noop,
  onToggleReaction,
  onReplyInDM,
  isPinned,
  onPin,
  pinLimitReached,
  isHighlighted,
  isDmFeatureEnabled = false,
  defaultExpanded = false,
}: CommunityPostCellProps) => {
  const [hideSensitiveContent, setHideSensitiveContent] = useState<boolean>(
    postData.isSensitiveContent,
  );
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const toggleExpand = useCallback(
    (trigger: ExpandCommentsTrigger) => {
      if (isExpanded) {
        setIsExpanded(false);
        return;
      }
      setIsExpanded(true);
      communityAnalytics.trackCommentsExpanded(trigger);
    },
    [isExpanded],
  );

  const handleCommentCountClick = useCallback(
    () => toggleExpand(ExpandCommentsTrigger.CommentsCount),
    [toggleExpand],
  );
  const handleAddCommentClick = useCallback(
    () => toggleExpand(ExpandCommentsTrigger.AddComment),
    [toggleExpand],
  );

  const handleHideSensitiveContent = useCallback(
    (ev: MouseEvent<HTMLButtonElement>) => {
      ev.preventDefault();
      ev.stopPropagation();
      setHideSensitiveContent(false);
    },
    [],
  );

  useTrackPostViewed({ postData, influencerId });

  return (
    <Box
      id={`post-${postData.id}`}
      className={classnames(
        'flex flex-col justify-start gap-4 p-4 transition-colors duration-500',
        {
          'bg-[#EDE5FC] rounded-[10px] my-2 mx-2': isPinned,
          'border-t first:border-0': !isPinned,
          'bg-purple-50': isHighlighted && !isPinned,
        },
      )}
    >
      {isPinned && (
        <Box className="flex items-center gap-2">
          <FontAwesomeIcon
            icon={faThumbtack}
            className="text-[#8A8E99] text-[10px]"
          />
          <Typography
            className="text-[12px]"
            sx={{ color: 'rgba(0,0,0,0.5)', fontWeight: 500 }}
          >
            Pinned
          </Typography>
        </Box>
      )}
      <PostHeader
        communityId={communityId}
        author={postData.author}
        createdAt={postData.createdAt}
        id={postData.id}
        isUserCommunityOwner={isUserCommunityOwner}
        onDelete={onDelete}
        onBlock={onBlock}
        onReplyInDM={onReplyInDM}
        isPinned={isPinned}
        onPin={onPin}
        pinLimitReached={pinLimitReached}
      />
      {hideSensitiveContent ? (
        <SensitiveContent
          isPostCell={true}
          handleHideSensitiveContent={handleHideSensitiveContent}
        />
      ) : (
        <>
          <PostContent text={postData.text} media={postData.media} />
          <PostReactions
            reactions={postData.reactions || []}
            onToggleReaction={onToggleReaction}
          />
          <PostFooter
            reactions={postData.reactions || []}
            onCommentCountClick={handleCommentCountClick}
            onAddCommentClick={handleAddCommentClick}
            isExpanded={isExpanded}
          />
          {isExpanded && (
            <CommunityCommentsSection
              influencerId={influencerId}
              communityId={communityId}
              postId={postData.id}
              post={postData}
              isUserCommunityOwner={isUserCommunityOwner}
              isDmFeatureEnabled={isDmFeatureEnabled}
              onBlock={onBlock}
            />
          )}
        </>
      )}
    </Box>
  );
};
